// src/components/common/WidgetCard.tsx
//
// Shared shell for the dashboard widgets: a hairline-bordered card ("3c
// hybrid" — no shadow, flat definition via borders) with a consistent
// uppercase micro-label header and an optional icon tile and trailing action.
// Styling comes from the theme tokens.
import React from 'react';
import { Card, CardContent, Box, Typography, alpha, useTheme } from '@mui/material';

interface WidgetCardProps {
  title: string;
  /** One line under the title saying what the figures measure, when the title alone is ambiguous. */
  description?: string;
  icon?: React.ReactNode;
  /** Palette main colour for the icon tile (e.g. theme.palette.dataAccent.main). */
  accent?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}

export const WidgetCard: React.FC<WidgetCardProps> = ({ title, description, icon, accent, action, children, sx }) => {
  const theme = useTheme();
  const tint = accent ?? theme.palette.dataAccent.dark;
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mb: 0,
        ...sx,
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.25, rowGap: 0.5, mb: description ? 0.5 : 1.5 }}>
          {icon && (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: theme.custom?.radius?.small ?? '8px',
                display: 'grid',
                placeItems: 'center',
                flex: 'none',
                color: tint,
                backgroundColor: alpha(tint, 0.12),
                '& svg': { fontSize: 16 },
              }}
            >
              {icon}
            </Box>
          )}
          {/* Eyebrow: neutral uppercase micro-label (theme `overline` —
              hybrid-3c.md rule 1 reserves teal for one thing at a time, not
              a label repeated on every card). */}
          <Typography
            variant="overline"
            sx={{ lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {action && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{action}</Box>}
        </Box>
        {description && (
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5 }}>
            {description}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
};
